import api from "../api/api";

/**
 * Submission attestation — client half.
 *
 * Backs the attested submission socket (server: assignments/attestation.py). The
 * flow is a per-connection challenge/response that a copied request (e.g. Postman)
 * cannot reproduce, because the proof must be computed fresh per connection:
 *
 *   1. POST /assignments/submission-ticket/  -> { ticket, nonce, expires_in }
 *      (single-use, 30s, bound to IP + Origin) — fetch right before opening the WS.
 *   2. Open WS with ?ticket=<ticket>; server sends { type:"attest_challenge", nonce }.
 *   3. Reply { type:"attest", proof } within 10s; server sends { type:"attest_ok" }.
 *
 * This is a cost barrier, not proof of client identity — the secret ships in the
 * bundle. It stops a separate client, not devtools on the real page.
 */

// Injected at build time from that environment's AWS secret
// (alias/{brand}/{env}/secret/key, key SUBMISSION_ATTESTATION_SECRET). Per brand+env.
// Never commit a value — this reads the env var only.
const ATTESTATION_SECRET =
  process.env.REACT_APP_SUBMISSION_ATTESTATION_SECRET;

/**
 * Request a single-use submission ticket. Must be fetched immediately before
 * opening the socket — it lives only 30s and is bound to IP + Origin.
 * @returns {Promise<{ticket:string,nonce:string,expires_in:number}>}
 */
export async function fetchSubmissionTicket({
  assignment_id,
  assignment_type,
  token,
}) {
  const res = await api.post(
    "assignments/submission-ticket/",
    { assignment_id, assignment_type },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
    },
  );
  return res?.data;
}

/**
 * proof = HMAC_SHA256(SECRET, `${nonce}|${ticket}|${origin}`) as lowercase hex.
 * `origin` defaults to window.location.origin (what the server binds against).
 * Requires a secure context — crypto.subtle is undefined over plain http, so every
 * proof would fail; callers should surface that rather than silently retry.
 */
export async function computeAttestationProof({
  nonce,
  ticket,
  origin = window.location.origin,
  secret = ATTESTATION_SECRET,
}) {
  if (!secret) {
    throw new Error(
      "Submission attestation secret missing (REACT_APP_SUBMISSION_ATTESTATION_SECRET).",
    );
  }
  if (!window.crypto?.subtle) {
    throw new Error(
      "crypto.subtle unavailable — attestation needs https or localhost.",
    );
  }
  const enc = new TextEncoder();
  const key = await window.crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await window.crypto.subtle.sign(
    "HMAC",
    key,
    enc.encode(`${nonce}|${ticket}|${origin}`),
  );
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
